import { Link } from 'react-router-dom';
import { Wrench, AlertCircle, CheckCircle } from 'lucide-react';

export default function InstallationGuide() {
  const steps = [
    {
      step: '01',
      title: 'Safety First',
      content: 'Ensure the firearm is completely unloaded, the action is open, and there is no ammunition in the firearm or in the immediate area before beginning installation. Never install any muzzle device on a loaded firearm.',
      warning: true,
    },
    {
      step: '02',
      title: 'Clean the Barrel',
      content: 'Clean the barrel at the mounting location to remove any oils, fouling, or debris. The bore must be clean for accurate positioning. Wipe with a clean patch or cloth and allow to dry completely.',
    },
    {
      step: '03',
      title: 'Dry Fit First',
      content: 'Before applying any Loctite, slide the brake onto the barrel to confirm proper fit. The brake should slide on smoothly and fit snugly. If it feels too tight or loose, verify your barrel measurements.',
    },
    {
      step: '04',
      title: 'Apply Loctite',
      content: 'Apply a thin, even bead of medium-strength Loctite (blue, #243) to the inside clamping surfaces of the brake. Do not use red (permanent) Loctite — this will make removal extremely difficult. The Loctite helps protect the barrel finish and prevents loosening under recoil.',
    },
    {
      step: '05',
      title: 'Position the Brake',
      content: 'Slide the brake onto the barrel at your desired position. For most applications, position the brake as close to the muzzle as possible while maintaining at least 0.5" clearance from the crown. Ensure ports are oriented correctly — the top ports should face up.',
    },
    {
      step: '06',
      title: 'Torque the Clamp Screws',
      content: 'Tighten the clamp screws evenly, alternating between screws in a cross pattern. Torque to approximately 30-35 in-lbs. Do not over-tighten. Check alignment visually — the bore must be aligned straight with the barrel bore.',
    },
    {
      step: '07',
      title: 'Allow Loctite to Cure',
      content: 'Allow the Loctite to cure for at least 24 hours before shooting. Full cure strength is reached at 24 hours at room temperature. Do not shoot the firearm during curing.',
    },
    {
      step: '08',
      title: 'Verify and Test Fire',
      content: 'After curing, inspect the brake one final time for proper alignment. Begin with a single shot to verify function, then proceed with normal use. Re-check screw torque after the first 50 rounds.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4">
            <Wrench size={18} className="text-orange-500" />
            <span className="font-inter text-xs text-orange-500 uppercase tracking-widest">Resources</span>
          </div>
          <h1 data-testid="installation-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-2">
            INSTALLATION INSTRUCTIONS
          </h1>
          <p className="font-inter text-sm text-zinc-500">Clamp-on muzzle brake installation guide</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-orange-500/10 border border-orange-500/30 p-5 mb-8 flex items-start gap-3">
          <AlertCircle size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-inter text-sm font-semibold text-orange-400 mb-1">Important Safety Notice</p>
            <p className="font-inter text-sm text-zinc-400">
              Always ensure the firearm is completely unloaded before attempting installation. If you are unsure of any step, consult a licensed gunsmith. Knowledge of local firearm laws is your responsibility.
            </p>
          </div>
        </div>

        <div className="space-y-4 mb-12">
          {steps.map((s) => (
            <div key={s.step} className={`flex gap-6 bg-[#111111] border p-6 ${s.warning ? 'border-orange-500/30' : 'border-white/10'}`}>
              <div className="font-bebas text-4xl text-orange-500/30 tracking-widest leading-none flex-shrink-0 w-14 text-center">
                {s.step}
              </div>
              <div>
                <h3 className="font-bebas text-xl text-white tracking-wider mb-2 flex items-center gap-2">
                  {s.title}
                  {s.warning && <AlertCircle size={14} className="text-orange-500" />}
                </h3>
                <p className="font-inter text-sm text-zinc-400 leading-relaxed">{s.content}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tips */}
        <div className="bg-[#111111] border border-white/10 p-8 mb-8">
          <h2 className="font-bebas text-2xl text-white tracking-wider mb-5">TIPS FOR SUCCESS</h2>
          <div className="space-y-3">
            {[
              'Use medium-strength blue Loctite only — never red',
              'A torque wrench ensures consistent, safe clamping force',
              'Ports should face forward and upward for best performance',
              'Measure your barrel one more time before final installation',
              'If the brake spins or loosens under fire, re-torque and reapply Loctite',
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="font-inter text-sm text-zinc-400">{tip}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Nav between resources */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <Link to="/resources/measuring" className="flex items-center justify-center gap-2 font-bebas text-sm tracking-widest border border-white/20 hover:border-white/40 text-white px-5 py-3 transition-colors">
            ← MEASURING GUIDE
          </Link>
          <Link to="/resources/faq" className="flex items-center justify-center gap-2 font-bebas text-sm tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 transition-colors">
            VIEW FAQ →
          </Link>
        </div>
      </div>
    </div>
  );
}
